// =============================================================================
// ingredientWastageRows.js — PURE, dependency-free shaper for the Meadowhall
// daily ingredient-wastage rows (In / Used / Wasted grams).
//
// Kept separate from ingredientWastageSync.js (which pulls in the Supabase
// client) so this core logic can be unit-tested in plain Node with zero setup —
// no browser, no network, no env. Given the resolved site uuid, the London
// trading day and the tile's per-ingredient figures, it returns the exact rows
// to UPSERT into ingredient_wastage_daily (migration 0620). No PIN and no staff
// name ever go in: staff is stamped server-side from the login token by the DB
// trigger.
// =============================================================================

// Coerce a tile field to a non-negative integer gram value. Blank / null / NaN /
// negative all collapse to 0 (the DB CHECK also refuses < 0; clamping here keeps
// one bad field from rejecting the whole upsert batch). Decimals are rounded —
// grams are logged whole on the tile.
function toGrams(x) {
  const n = Math.round(Number(x));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// entries: { [ingredient]: { in, used, wasted } } — the SAME object the tile
// holds in state, values in grams (numbers or blank).
// day: the London trading date 'YYYY-MM-DD'.
// siteId: the resolved sites.id uuid.
// keepZeroFor: optional Set/array of ingredient names the staffer EXPLICITLY
//   logged this session (e.g. "checked the bin, nothing wasted"). An ingredient
//   in this set is written even when all three figures are 0 — that affirmative
//   "zero waste today" row is the whole point of logging a clean day, and the CC
//   reads a 0/0/0 row as "logged, nothing wasted" (distinct from a blank, never-
//   logged day). Omit it and the old behaviour holds.
//
// An ingredient with nothing entered (all three 0/blank) AND not in keepZeroFor
// is dropped — a save should never write an all-zero row for an ingredient the
// staffer never touched. An ingredient with ANY positive figure is always
// written in full (a legit "in 4000, used 4000, wasted 0" keeps wasted_g = 0).
export function ingredientWastageRowsFrom({ siteId, day, entries, keepZeroFor } = {}) {
  if (!siteId || !entries || typeof entries !== "object") return [];
  const keep = keepZeroFor instanceof Set ? keepZeroFor : new Set(keepZeroFor || []);
  return Object.keys(entries)
    .map((ingredient) => {
      const v = entries[ingredient];
      if (!ingredient || !v || typeof v !== "object") return null;
      const in_g = toGrams(v.in);
      const used_g = toGrams(v.used);
      const wasted_g = toGrams(v.wasted);
      if (in_g === 0 && used_g === 0 && wasted_g === 0 && !keep.has(ingredient)) return null;
      return {
        site_id: siteId,
        event_date: day,
        ingredient: String(ingredient),
        in_g,
        used_g,
        wasted_g,
      };
    })
    .filter(Boolean);
}

// Merge a session's edits onto a FRESH read of today's row. `dirty` is a Set of
// "ingredient|field" cells the staffer actually touched this session; only those
// override — every other field takes the latest SAVED value from `fresh`. This
// is what makes a partial re-save safe across devices/time: a stale mount
// snapshot can never wipe figures another tablet (or the CC) wrote after this
// tile mounted. Only ingredients with at least one touched field are returned —
// untouched rows are left untouched.
export function mergeForSave(entries, fresh, dirty) {
  const out = {};
  const dirtySet = dirty instanceof Set ? dirty : new Set(dirty || []);
  const dirtyIngs = new Set([...dirtySet].map((k) => String(k).split("|")[0]));
  dirtyIngs.forEach((ing) => {
    const e = (entries && entries[ing]) || {};
    const f = (fresh && fresh[ing]) || {};
    out[ing] = {
      in:     dirtySet.has(ing + "|in")     ? e.in     : f.in,
      used:   dirtySet.has(ing + "|used")   ? e.used   : f.used,
      wasted: dirtySet.has(ing + "|wasted") ? e.wasted : f.wasted,
    };
  });
  return out;
}

// Physical-sanity check for one ingredient's figures. You can't USE or WASTE
// more grams than came IN — that's nonsense data the cost report must never
// accept. Only checked once In is entered (>0): an In-less "just log the waste"
// entry has no intake to exceed, so it isn't blocked. Returns the FIRST offending
// field + a message, or null if the figures are fine. The tile uses this to flag
// the field and block Save until it's fixed.
export function ingredientWastageError(v) {
  if (!v) return null;
  const inG = Number(v.in) || 0;
  const used = Number(v.used) || 0;
  const wasted = Number(v.wasted) || 0;
  if (inG > 0 && used > inG)   return { field: "used",   msg: "Used can't be more than In" };
  if (inG > 0 && wasted > inG) return { field: "wasted", msg: "Wasted can't be more than In" };
  return null;
}

// Exposed for the unit test only.
export const _toGrams = toGrams;
